import { useEffect, useState } from "react";
import { apiFetch, ApiError } from "../api/client.js";

interface HealthResponse {
  ok: boolean;
  sub: string | null;
}

/**
 * Hidden route at /app/debug/health used to verify the JWT-authorized API
 * round-trip end-to-end. Linked from nowhere on purpose — visit by URL.
 */
export function DebugHealth() {
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  const [data, setData] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<HealthResponse>("/health")
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setStatus("ok");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setError(`HTTP ${err.status}: ${JSON.stringify(err.body)}`);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("Unknown error");
        }
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-xl font-semibold">Debug — /health</h1>
      <p className="text-sm text-slate-600">
        Calls the API Gateway <code>GET /health</code> route with the current Cognito ID token. Used
        to verify auth round-trips end-to-end.
      </p>
      {status === "loading" && <p>Calling API…</p>}
      {status === "ok" && data && (
        <pre className="rounded bg-slate-100 p-3 text-sm overflow-x-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
      {status === "error" && (
        <pre className="rounded bg-rose-50 p-3 text-sm text-rose-900 overflow-x-auto">{error}</pre>
      )}
    </div>
  );
}
